'use client';

import { IGoodsListPageInitialData } from '@/api/types/product';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import FaqAccordion from '../../components/FaqAccordion';
import StaticFaqForSeo from '../../components/StaticFaqForSeo';
import AdvantagePart from '../components/AdvantagePart';
import Banner, { BannerData } from '../components/Banner';
import ComparisonPart from '../components/ComparisonPart';
import ForLifeStyles from '../components/ForLifeStyles';
import GoodsListPart from '../components/GoodsListPart';
import IntroPart from '../components/IntroPart';
import StepsPart from '../components/StepsPart';
import { useGetInitialData } from '../hooks/useGetInitialData';
import { useTrack } from '../hooks/useTrack';

interface PageData {
  seo_data?: any;
  product_list?: {
    id: number;
    [key: string]: any;
  };
  banner?: BannerData;
  intro?: {
    title: string;
    description: string;
    leg_img?: string;
    frame_img?: string;
    image?: string;
    secondary_title: string;
    secondary_description: string;
    aspect_ratio?: string;
    frame_color?: {
      isSolid: boolean;
      bgColor: string;
      bgColor1?: string;
      transparency: number;
      transparency1?: number;
    };
  };
  whyChoose?: {
    title: string;
    data_array: Array<{
      image: string;
      title: string;
      description: string;
    }>;
  };
  for_lifestyles?: {
    title: string;
    title_m?: string;
    data_array: Array<{
      image: string;
      image_m?: string;
      btn_word?: string;
      btn_word_m?: string;
      title?: string;
      title_m?: string;
    }>;
    aspect_ratio?: string;
    aspect_ratio_m?: string;
  };
  [key: string]: any;
}

interface ServerInitialData {
  pageData: PageData | null;
  seoInfo: any;
  seoData: any;
  error: string | null;
  initialData: IGoodsListPageInitialData | null;
}

interface ClientPageProps {
  serverData: ServerInitialData;
  searchParams?: {
    sort?: string;
    filter?: string;
    adp?: string;
    page?: string;
  };
}

export default function ProgressiveGlassesClientPage({
  serverData,
  searchParams,
}: ClientPageProps) {
  // 使用hook获取数据，传入服务端预获取的数据
  const { pageData, loading, error } = useGetInitialData('progressive_page', serverData);

  // 使用埋点追踪Hook
  const { trackPageView, trackMktExp } = useTrack();

  // App相关状态管理
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);
  // const token = useStore((state) => state.token);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setInAppH5 = useStore((state) => state.setInAppH5);

  // 设置各区域的曝光检测
  const [introRef, introInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [comparisonAdvantageRef, comparisonAdvantageInView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const [forLifeStylesRef, forLifeStylesInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [stepsRef, stepsInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [faqRef, faqInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  // 检测App环境并初始化
  useEffect(() => {
    // 检测是否在App内嵌环境
    const isInApp = navigator.userAgent.includes('nextmar-xms-app');
    setInAppH5(isInApp);
  }, [setInAppH5]);

  // App通信初始化
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
      window.addEventListener('appView', trackPageView);
      return () => {
        window.removeEventListener('appView', trackPageView);
      };
    }
  }, [inAppH5]);

  // 页面加载完成后触发页面浏览埋点
  useEffect(() => {
    if (!loading && !error && pageData) {
      trackPageView();
    }
  }, [loading, error, pageData, trackPageView]);

  // 处理各区域的曝光埋点
  useEffect(() => {
    if (introInView) {
      trackMktExp('什么是渐进镜');
    }
  }, [introInView, trackMktExp]);

  useEffect(() => {
    if (comparisonAdvantageInView) {
      trackMktExp('标准渐进镜');
    }
  }, [comparisonAdvantageInView, trackMktExp]);

  useEffect(() => {
    if (forLifeStylesInView) {
      trackMktExp('适用场景');
    }
  }, [forLifeStylesInView, trackMktExp]);

  useEffect(() => {
    if (stepsInView) {
      trackMktExp('如何购买');
    }
  }, [stepsInView, trackMktExp]);

  useEffect(() => {
    if (faqInView) {
      trackMktExp('FAQ');
    }
  }, [faqInView, trackMktExp]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('App登录token:', _token);
          if (_token !== 'ClearToken') {
            setToken(_token);
            // 获取用户信息
            await refreshAccountInfo();
          } else {
            // 清空当前用户信息
            setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (e) {
      logger.error('App通信初始化失败:', e);
    }
  };

  // 处理wishlist状态变化

  // 组件卸载时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">No data available</div>
      </div>
    );
  }

  return (
    <div>
      {/* Banner 区域 */}
      {pageData?.banner && (
        <Banner bannerData={pageData.banner} ts={1} from="progressive-glasses" />
      )}

      {/* Intro 区域 - 对应Vue中的"什么是渐进镜"曝光 */}
      {pageData?.intro && (
        <div ref={introRef}>
          <IntroPart introData={pageData.intro} />
        </div>
      )}

      {/* ComparisonPart 和 AdvantagePart 区域 - 对应Vue中的"标准渐进镜"曝光 */}
      {(pageData?.comparison || pageData?.advantage) && (
        <div ref={comparisonAdvantageRef}>
          {pageData?.comparison && <ComparisonPart comparisonData={pageData.comparison} />}
          {pageData?.advantage && <AdvantagePart advantageData={pageData.advantage} />}
        </div>
      )}

      {/* ForLifeStyles 区域 - 对应Vue中的"适用场景"曝光 */}
      {pageData?.for_lifestyles && (
        <div ref={forLifeStylesRef}>
          <ForLifeStyles forLifeStylesData={pageData.for_lifestyles} />
        </div>
      )}

      {/* Steps 区域 - 对应Vue中的"如何购买"曝光 */}
      <div ref={stepsRef}>
        <StepsPart stepsData={pageData.steps} />
      </div>

      {/* GoodsListPart 区域 */}
      {pageData?.product_list && (
        <GoodsListPart
          goodsListData={pageData.product_list}
          initialData={serverData.initialData}
          searchParams={searchParams}
        />
      )}

      {/* FAQ 区域 - 对应Vue中的"FAQ"曝光 */}
      {pageData?.faq && (
        <div ref={faqRef}>
          <FaqAccordion faqData={pageData.faq} />
        </div>
      )}
      <StaticFaqForSeo faqData={pageData.faq} />
    </div>
  );
}
